// frontend/src/pages/builder/ColumnsWell.jsx
// Phase 3 step 7d-2 (decision D11): the Table visual's one slot. Every column
// is a chip in display order. Fields drop in from the list (on a chip: before
// it; on the slot: at the end); chips drag to reorder the same way; the grip
// also moves a chip with the left and right arrow keys, so the order never
// depends on a mouse. Each chip has one picker -- "Don't summarize" or an
// aggregation -- and a date chip a grain picker. Rules in tableColumns.js.
import React, { useState } from 'react';
import { GripVertical, X } from 'lucide-react';
import { PanelHint, PanelLabel } from '@/components/ui/panel.jsx';
import { GRAINS } from './builderSpec';
import { GROUP, useOptions } from './tableColumns';
import { CHIP_MIME, ChipPicker, FIELD_MIME } from './Wells';

const payload = (e) => {
  const key = e.dataTransfer.getData(FIELD_MIME);
  if (key) return { field: key };
  try {
    const chip = JSON.parse(e.dataTransfer.getData(CHIP_MIME) || 'null');
    return chip && chip.kind === 'column' && typeof chip.key === 'string' ? { column: chip.key } : null;
  } catch (err) { return null; }
};

const accepts = (e) => {
  const t = Array.from(e.dataTransfer.types || []);
  return t.includes(FIELD_MIME) || t.includes(CHIP_MIME);
};

function ColumnChip({ chip, field, first, last, onDropBefore, onUse, onGrain, onRemove, onNudge }) {
  const [over, setOver] = useState(false);
  const name = field ? field.display_name : chip.item.field || chip.label;
  const use = chip.kind === 'dimension' ? GROUP : chip.item.aggregation;
  const options = field ? useOptions(field) : [];
  const grain = chip.kind === 'dimension' ? chip.item.grain : null;
  return (
    <span
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData(CHIP_MIME, JSON.stringify({ kind: 'column', key: chip.key }));
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragOver={(e) => { if (accepts(e)) { e.preventDefault(); e.stopPropagation(); setOver(true); } }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        setOver(false);
        const p = payload(e);
        if (!p) return;
        e.preventDefault(); e.stopPropagation();
        onDropBefore(p, chip.key);
      }}
      title="Drag to reorder"
      className={`inline-flex h-7 max-w-full cursor-grab items-center gap-1 rounded-full bg-[var(--ui-chip)] pl-0.5 pr-1 text-[12.5px] text-[var(--ui-text)] active:cursor-grabbing ${
        over ? 'shadow-[-3px_0_0_0_var(--ui-selected)]' : ''}`}
    >
      <button
        type="button"
        aria-label={`Move ${name}. Use the left and right arrow keys.`}
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft' && !first) { e.preventDefault(); onNudge(chip.key, -1); }
          if (e.key === 'ArrowRight' && !last) { e.preventDefault(); onNudge(chip.key, 1); }
        }}
        className="rounded-full p-0.5 text-[var(--ui-text-muted)] hover:text-[var(--ui-text)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
      >
        <GripVertical className="h-3 w-3" aria-hidden="true" />
      </button>
      {options.length > 1 ? (
        <ChipPicker label={`How to show ${name}`} value={use} options={options} onChange={(v) => onUse(chip.key, v)} />
      ) : null}
      <span className="truncate">{name}</span>
      {grain && (
        <ChipPicker label={`Group ${name} by`} value={grain} options={GRAINS.map((g) => [g, g])} onChange={(v) => onGrain(chip.key, v)} />
      )}
      <button
        type="button" onClick={() => onRemove(chip.key)} aria-label={`Remove ${chip.label}`}
        className="rounded-full p-0.5 text-[var(--ui-text-muted)] hover:bg-white/10 hover:text-[var(--ui-text)]"
      >
        <X className="h-3 w-3" aria-hidden="true" />
      </button>
    </span>
  );
}

/**
 * chips: from tableColumns.orderedChips. onDrop({field}|{column}, beforeKey)
 * places a field or moves a chip before another (null = at the end).
 */
export default function ColumnsWell({ chips, fieldsByKey, showCount, onDrop, onUse, onGrain, onRemove, onNudge }) {
  const [over, setOver] = useState(false);
  const empty = chips.length === 0;
  return (
    <div>
      <PanelLabel id="well-columns-label">Columns</PanelLabel>
      <div
        role="group" aria-labelledby="well-columns-label"
        onDragOver={(e) => {
          if (!accepts(e)) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = Array.from(e.dataTransfer.types).includes(CHIP_MIME) ? 'move' : 'copy';
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          setOver(false);
          const p = payload(e);
          if (!p) return;
          e.preventDefault();
          onDrop(p, null);
        }}
        className={`flex min-h-[42px] flex-wrap items-center gap-1.5 rounded-xl p-2 transition-[background-color,box-shadow] ${
          over ? 'bg-[var(--ui-inset-hover)] shadow-[inset_0_0_0_1.5px_var(--ui-selected)]'
            : empty ? 'border border-dashed border-[var(--ui-dash)] pl-3'
              : 'bg-[var(--ui-inset)]'}`}
      >
        {empty && <span className="text-[12.5px] text-[var(--ui-text-muted)]">Drop or click fields, in the order you want them</span>}
        {chips.map((c, i) => (
          <ColumnChip key={c.key} chip={c} field={fieldsByKey[c.item.field]} first={i === 0} last={i === chips.length - 1}
            onDropBefore={onDrop} onUse={onUse} onGrain={onGrain} onRemove={onRemove} onNudge={onNudge} />
        ))}
        {!empty && showCount && (
          <span title="Add a number to replace the count"
            className="inline-flex h-7 items-center rounded-full border border-dashed border-[var(--ui-dash)] px-2.5 text-[12.5px] text-[var(--ui-text-muted)]">
            Count of records
          </span>
        )}
      </div>
      <PanelHint>
        {empty ? 'Text, dates and ids group the rows; numbers are summed. Each chip’s picker changes that.'
          : 'Drag a chip to reorder. Click a column heading in the table to sort by it.'}
      </PanelHint>
    </div>
  );
}